import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  LightBulbIcon,
  DocumentTextIcon,
  UserGroupIcon,
  BookOpenIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";

/**
 * MobileWhy1 - Versión móvil ULTRA optimizada
 * - NO carga imágenes pesadas inicialmente
 * - Usa gradientes como placeholder
 * - Lazy loading con Intersection Observer
 * - Imágenes solo se cargan cuando están visibles
 */

// Componente de imagen optimizada con lazy loading real
const LazyImage: React.FC<{ src: string; alt: string; className?: string }> = ({
  src,
  alt,
  className,
}) => {
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);

  // Usar Intersection Observer para lazy loading real
  const imgRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "100px" } // Cargar 100px antes de que sea visible
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={imgRef} className="w-full h-full">
      {/* Placeholder gradient mientras carga */}
      {!loaded && (
        <div className="absolute inset-0 bg-gradient-to-br from-[#00e9be]/20 to-[#00342d]/30 animate-pulse" />
      )}
      {/* Solo cargar imagen cuando está en viewport */}
      {inView && (
        <img
          src={src}
          alt={alt}
          className={`${className} ${
            loaded ? "opacity-90" : "opacity-0"
          } transition-opacity duration-300`}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
        />
      )}
    </div>
  );
};

const MobileWhy1: React.FC = () => {
  const { t } = useTranslation();

  const features = [
    {
      title: t(
        "home.why.card1.title",
        "Level Up with personalized on-demand continuing education and mentorship"
      ),
      description: t(
        "home.why.card1.description",
        "Enhance your veterinary professional development. Learn faster at your pace with expert guidance and apply it immediately in clinical practice"
      ),
      icon: LightBulbIcon,
      image: "/images/home/why/report.webp",
      tag: t("home.why.card1.label", "Grow fast"),
      color: "text-[#00342d]",
    },
    {
      title: t(
        "home.why.card2.title",
        "Instant, evidence-backed answers for your cases"
      ),
      description: t(
        "home.why.card2.description",
        "Get clinical decision support with the latest, referenced veterinary information in seconds to guide your next move with confidence"
      ),
      icon: DocumentTextIcon,
      image: "/images/home/why/ai-insight.webp",
      tag: t("home.why.card2.label", "Second opinion in seconds"),
      color: "text-[#00342d]",
    },
    {
      title: t(
        "home.why.card3.title",
        "Instantly generate clean, structured, personalized reports"
      ),
      description: t(
        "home.why.card3.description",
        "Streamline veterinary practice management. Consult as usual, speak your notes with AI transcription, and we write the rest automatically"
      ),
      icon: UserGroupIcon,
      image: "/images/home/why/network.webp",
      tag: t("home.why.card3.label", "Save hours every day"),
      color: "text-[#00342d]",
    },
    {
      title: t(
        "home.why.card4.title",
        "Read only what matters with concise summaries of referred patients"
      ),
      description: t(
        "home.why.card4.description",
        "See the patient’s story in one clear timeline"
      ),
      icon: BookOpenIcon,
      image: "/images/home/why/Library.webp",
      tag: t("home.why.card4.label", "Your private case assistant"),
      color: "text-[#00342d]",
    },
    {
      title: t("home.why.card5.title", "Use our materials and create your own"),
      description: t(
        "home.why.card5.description",
        "Access veterinary templates, clinical protocols, and medical documents anywhere for consistent, evidence-based care across any clinic"
      ),
      icon: ChartBarIcon,
      image: "/images/home/why/summary.webp",
      tag: t("home.why.card5.label", "Build your own library."),
      color: "text-[#00342d]",
    },
    {
      title: t(
        "home.why.card6.title",
        "Connect with the global veterinary community and stay up to date in minutes."
      ),
      description: t(
        "home.why.card6.description",
        "Ask once and get fast, peer-verified answers with real-time updates"
      ),
      icon: UserGroupIcon,
      image: "/images/home/why/VetTalk.webp",
      tag: t("home.why.card6.label", "Expand your network"),
      color: "text-[#00342d]",
    },
  ];

  return (
    <section className="py-16 relative overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2
            className="text-3xl sm:text-4xl font-bold text-[#00342d] mb-6 tracking-tight"
            style={{ fontFamily: "'Bruno Ace', sans-serif" }}
          >
            {t("home.why.title", "Why Choose VettConsult?")}
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            {t(
              "home.why.subtitle",
              "Experience the future of veterinary practice with our cutting-edge features."
            )}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group relative overflow-hidden rounded-3xl bg-white border border-gray-100 shadow-sm hover:shadow-xl transition-shadow duration-300 h-80"
            >
              {/* Imagen con lazy loading real */}
              <div className="absolute inset-0 z-0">
                <LazyImage
                  src={feature.image}
                  alt={feature.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              </div>

              <div className="relative z-10 h-full flex flex-col justify-end p-6">
                <div className="flex items-center justify-between mb-4">
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center backdrop-blur-xl bg-white/20 border border-white/30 shadow-lg ${feature.color}`}
                  >
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-white/20 backdrop-blur-md border border-white/30 text-white">
                    {feature.tag}
                  </span>
                </div>

                <h3 className="text-xl font-bold text-white mb-2 leading-tight">
                  {feature.title}
                </h3>
                <p className="text-gray-200 text-sm font-medium line-clamp-3">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MobileWhy1;
